import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { SEO } from '../../components/SEO';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { CONTACT } from '../../constants/images';

export const LegFootCare = () => {
  const conditions = [
    {
      title: 'Peripheral Artery Disease (PAD) — Angioplasty & Stenting',
      description: 'PAD occurs when the arteries supplying blood to the legs become narrowed or blocked due to atherosclerosis. Especially common in patients with diabetes, hypertension, high cholesterol, and smoking history.',
      symptoms: ['Claudication — cramping pain on walking', 'Rest pain — severe pain in foot, worse at night', 'Non-healing wounds or ulcers on foot/toes', 'Blackening or gangrene of toes', 'Cold, pale, or shiny skin on affected leg'],
      treatments: ['Percutaneous Transluminal Angioplasty (PTA) — balloon opens blocked artery', 'Arterial Stenting — metallic stent keeps artery patent', 'Same-day or next-morning discharge']
    },
    {
      title: 'Varicose Veins Treatment',
      description: 'Varicose veins are enlarged, twisted veins that develop when one-way valves inside leg veins fail. Common in people with long-standing jobs - Chefs, Waiters, Security guards, Teachers.',
      symptoms: ['Prominent, ropy veins visible under skin', 'Heaviness, aching, or fatigue after standing', 'Ankle swelling by end of day', 'Skin darkening (pigmentation) around ankle', 'Venous ulcers — slow-healing wounds', 'Itching or burning over varicose veins'],
      treatments: ['EVLT (Endovenous Laser Treatment) — laser fibre seals faulty vein', 'VenaSeal (Cyanoacrylate Glue Therapy) — medical-grade glue, no heat, no compression stockings', 'Sclerotherapy — chemical injection for smaller varicose veins', 'Walk-in, walk-out procedure']
    },
    {
      title: 'Deep Vein Thrombosis (DVT) & IVC Filter',
      description: 'DVT is a blood clot in a deep leg vein. The clot can break off and travel to the lungs causing a pulmonary embolism — a potentially fatal emergency.',
      symptoms: ['Sudden swelling of one leg', 'Warmth and redness over swollen area', 'Breathlessness or chest pain (PE warning signs)'],
      treatments: ['IVC Filter Placement — traps clots before they reach lungs', 'Catheter-Directed Thrombolysis — clot-dissolving medicine via catheter', 'Venous Stenting for May-Thurner Syndrome']
    },
    {
      title: 'Diabetic Foot Salvage — Below-Knee Angioplasty',
      description: 'In advanced diabetes, poor micro-circulation leads to non-healing ulcers and gangrene. Dr. Punit specialises in infrapopliteal (below-knee) and pedal angioplasty — saving limbs from amputation.',
      symptoms: ['Non-healing ulcer or wound on foot/toes', 'Blackened or gangrenous toes', 'Rest pain or cold pale foot', 'Foul-smelling wound with discharge'],
      treatments: ['Below-Knee Angioplasty — opens tiny arteries around ankle', 'Sub-intimal angioplasty technique', 'Pedal-plantar loop technique', 'Available at Bombay Hospital, Mumbai', 'Has saved countless limbs from amputation']
    }
  ];

  const faqs = [
    { q: 'Is varicose vein laser treatment painful in Mumbai?', a: 'EVLT is performed under local anaesthesia. Most patients feel minimal discomfort. Most walk out the same day and resume activities the next day.' },
    { q: 'What is the difference between EVLT and VenaSeal for varicose veins?', a: 'EVLT uses laser heat; VenaSeal uses medical-grade glue. VenaSeal requires fewer injections and no prolonged compression stockings.' },
    { q: 'Can blocked leg arteries be treated without surgery in Mumbai?', a: 'Yes. Angioplasty and stenting opens blocked leg arteries through a small groin puncture — no general anaesthesia, no surgical incision.' },
    { q: 'Can a diabetic foot be saved without amputation in Mumbai?', a: 'In many cases, yes. Below-knee angioplasty restores blood flow to the foot, allowing wounds to heal. Early referral is critical.' }
  ];

  return (
    <>
      <SEO 
        title="Varicose Veins & PAD Treatment in Mumbai | Dr. Punit Mahajan"
        description="Minimally invasive treatment for varicose veins, peripheral artery disease, DVT & diabetic foot in Mumbai. Laser ablation, angioplasty & stenting by Dr. Punit Mahajan."
      />

      <section className="bg-gradient-to-br from-blue-950 to-slate-900 text-white py-20 relative overflow-hidden"><div className="absolute inset-0 pattern-hero opacity-20"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 animate-text-reveal">Leg & Foot Vascular Conditions</h1>
          <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto animate-text-reveal" style={{ animationDelay: "300ms" }}>Expert Interventional Treatment in Mumbai — Without Open Surgery</p>
        </div>
      </section>

      <section className="py-16 bg-cream-textured relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose max-w-none mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Introduction</h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Leg vascular disease is among the most common and most undertreated conditions in India. From varicose veins causing daily discomfort to blocked arteries threatening limb loss in diabetics, the spectrum is wide. Dr. Punit Mahajan treats all major leg vascular conditions using catheter-based, image-guided techniques — without open surgery, without general anaesthesia, and with same-day or next-day discharge in most cases.
            </p>
          </div>

          <div className="space-y-8">
            {conditions.map((condition, index) => (
              <Card key={index} className="card-gold-hover transition-all bg-white">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-blue-900 mb-4">{condition.title}</h3>
                  <p className="text-slate-600 mb-6 leading-relaxed">{condition.description}</p>
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3">Symptoms:</h4>
                      <ul className="space-y-2">
                        {condition.symptoms.map((s, idx) => (
                          <li key={idx} className="flex items-start"><CheckCircle className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" /><span className="text-slate-600">{s}</span></li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3">Treatments:</h4>
                      <ul className="space-y-2">
                        {condition.treatments.map((t, idx) => (
                          <li key={idx} className="flex items-start"><ArrowRight className="w-5 h-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" /><span className="text-slate-600">{t}</span></li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-cream-stripes relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {faqs.map((faq, idx) => (
              <Card key={idx} className="border-l-4 border-amber-500 bg-white">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">{faq.q}</h3>
                  <p className="text-slate-600">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-br from-blue-900 to-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="text-3xl md:text-4xl font-bold">Get Expert Leg & Foot Vascular Care</h2>
          <p className="text-xl text-slate-300">Book a consultation with Dr. Punit Mahajan in Mumbai</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${CONTACT.phone}`}><Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white">Call {CONTACT.phone}</Button></a>
            <Link to="/contact"><Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900">Book Online</Button></Link>
          </div>
        </div>
      </section>
    </>
  );
};
